import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

/**
 * Admin: Metered Billing.
 *
 * Two deliberate separations are visible in this UI, because conflating them is
 * how billing goes wrong:
 *
 *  - the per-credit PRICE is changed through the versioned rate endpoint, never
 *    through config, and the confirmation states that existing and historical
 *    usage is not repriced;
 *  - the collection THRESHOLD is not the user's hard cap. It lives on Metronome
 *    contracts, so changing it schedules a sync and is shown as pending until
 *    every contract has it -- never as already deployed.
 */
const MeteredBillingAdmin = () => {
  const [config, setConfig] = useState(null);
  const [rates, setRates] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState('');
  const [newPrice, setNewPrice] = useState('');
  const [confirmPrice, setConfirmPrice] = useState(false);
  const [form, setForm] = useState({
    billing_threshold_amount: '',
    minimum_user_cap_amount: '',
    default_user_cap_amount: '',
    maximum_user_cap_amount: '',
  });

  const authHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }), []);

  const load = useCallback(async () => {
    try {
      setError('');
      const [cfg, rateList, accountList, healthRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/metered/config`, authHeaders()),
        axios.get(`${API_URL}/api/admin/metered/rates`, authHeaders()),
        axios.get(`${API_URL}/api/admin/metered/accounts`, authHeaders()),
        axios.get(`${API_URL}/api/admin/metered/health`, authHeaders()),
      ]);
      setConfig(cfg.data);
      setRates(rateList.data.rates || []);
      setAccounts(accountList.data.accounts || []);
      setHealth(healthRes.data);
      setForm({
        billing_threshold_amount: cfg.data.billing_threshold_amount || '',
        minimum_user_cap_amount: cfg.data.minimum_user_cap_amount || '',
        default_user_cap_amount: cfg.data.default_user_cap_amount || '',
        maximum_user_cap_amount: cfg.data.maximum_user_cap_amount || '',
      });
    } catch (err) {
      const body = err.response?.data || {};
      const detail = body.error || 'Failed to load metered billing.';
      setError(body.code === 'metered_config_not_ready'
        ? `${detail} Save the collection threshold and cap policy first.`
        : detail);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  const saveConfig = async () => {
    setBusy('config');
    setError('');
    setMessage('');
    const required = [
      ['billing_threshold_amount', 'Billing threshold'],
      ['default_user_cap_amount', 'Default user cap'],
      ['minimum_user_cap_amount', 'Minimum user cap'],
      ['maximum_user_cap_amount', 'Maximum user cap'],
    ];
    for (const [key, label] of required) {
      if (!String(form[key]).trim()) {
        setError(`${label} is required.`);
        setBusy('');
        return;
      }
    }
    try {
      const payload = {};
      Object.entries(form).forEach(([key, value]) => {
        // Amounts are sent as strings: a number here would round.
        if (String(value).trim() !== '') payload[key] = String(value).trim();
      });
      const { data } = await axios.put(
        `${API_URL}/api/admin/metered/config`, payload, authHeaders());
      const pending = data.threshold?.pending_contract_syncs;
      setMessage(pending
        ? `Saved. ${pending} contract(s) queued for threshold sync -- not yet `
          + 'deployed to every account.'
        : 'Saved.');
      await load();
    } catch (err) {
      const body = err.response?.data || {};
      const detail = body.error || 'Failed to save configuration.';
      setError(body.code === 'metered_config_not_ready'
        ? `${detail} Fill in all threshold and cap fields, then save.`
        : detail);
    } finally {
      setBusy('');
    }
  };

  const changePrice = async () => {
    setBusy('price');
    setError('');
    setMessage('');
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/metered/rates`,
        { unit_price: String(newPrice).trim() }, authHeaders());
      setMessage(`New rate active: ${data.unit_price} per credit.`);
      setNewPrice('');
      setConfirmPrice(false);
      await load();
    } catch (err) {
      const body = err.response?.data || {};
      const detail = body.error
        || 'Failed to change the rate. The previous price is still active.';
      setError(body.code === 'metered_config_not_ready'
        ? `${detail} Save the collection threshold and cap policy first (step 1).`
        : detail);
    } finally {
      setBusy('');
    }
  };

  if (loading) {
    return <div className="text-sm text-gray-500 dark:text-gray-400">
      Loading metered billing...
    </div>;
  }

  const label = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';
  const input = 'w-full px-3 py-2 rounded-lg border border-gray-300 '
    + 'dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 '
    + 'dark:text-white text-sm';
  const card = 'p-4 rounded-xl border border-gray-200 dark:border-gray-700 '
    + 'bg-white dark:bg-gray-800/40 space-y-3';

  const policyReady = config?.policy_configured ?? Boolean(
    config?.billing_threshold_amount
    && config?.default_user_cap_amount
    && config?.minimum_user_cap_amount
    && config?.maximum_user_cap_amount,
  );
  const rateReady = config?.rate_configured ?? config?.unit_price != null;
  const setupComplete = policyReady && rateReady;

  const setupSteps = [
    { label: 'Deployment flag on (METERED_USAGE_FEATURE_ENABLED)', done: config?.feature_enabled },
    { label: 'Collection threshold and cap policy saved', done: policyReady },
    { label: 'Per-credit price published', done: rateReady },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          Metered Billing
        </h2>
        <span className={`text-xs px-2 py-0.5 rounded-full ${config?.feature_enabled
          ? 'bg-green-500/20 text-green-700 dark:text-green-400'
          : 'bg-gray-500/20 text-gray-600 dark:text-gray-400'}`}>
          {config?.feature_enabled ? 'feature enabled' : 'feature disabled'}
        </span>
      </div>

      <div className={`${card} border-brand-purple/30 bg-brand-purple/5`}>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">
          Setup checklist
        </h3>
        <p className="text-xs text-gray-600 dark:text-gray-400">
          Users cannot enable On-Demand Usage until every step below is complete.
          Configure in order: policy first, then price.
        </p>
        <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700 dark:text-gray-300">
          {setupSteps.map((step) => (
            <li key={step.label} className={step.done ? 'text-green-700 dark:text-green-400' : ''}>
              {step.done ? '[done] ' : '[pending] '}{step.label}
            </li>
          ))}
        </ol>
        {setupComplete && (
          <p className="text-sm text-green-700 dark:text-green-400">
            Ready for users. They can enable metered usage under Credits &amp; Billing.
          </p>
        )}
      </div>

      {error && (
        <div className="bg-red-500/20 border border-red-500/30 text-red-600 dark:text-red-400 px-3 py-2 rounded text-sm">
          {error}
        </div>
      )}
      {message && (
        <div className="bg-green-500/20 border border-green-500/30 text-green-700 dark:text-green-400 px-3 py-2 rounded text-sm">
          {message}
        </div>
      )}

      {/* Step 1: threshold and cap policy (required before price) ---------- */}
      <div className={card}>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">
          Step 1: Collection threshold &amp; cap policy
        </h3>
        <p className="text-xs text-gray-600 dark:text-gray-400">
          The collection threshold is when an interim invoice may be raised. It is
          not a user&apos;s spending limit -- paying a threshold invoice does not
          restore cap headroom. All four fields are required before you can set a
          price or offer metered usage to users.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className={label} htmlFor="threshold">Billing threshold (USD)</label>
            <input id="threshold" className={input} type="text" inputMode="decimal"
              placeholder="50"
              value={form.billing_threshold_amount}
              onChange={(e) => setForm({ ...form,
                billing_threshold_amount: e.target.value })} />
          </div>
          <div>
            <label className={label} htmlFor="cap-default">Default user cap (USD)</label>
            <input id="cap-default" className={input} type="text" inputMode="decimal"
              placeholder="100"
              value={form.default_user_cap_amount}
              onChange={(e) => setForm({ ...form,
                default_user_cap_amount: e.target.value })} />
          </div>
          <div>
            <label className={label} htmlFor="cap-min">Minimum user cap (USD)</label>
            <input id="cap-min" className={input} type="text" inputMode="decimal"
              placeholder="10"
              value={form.minimum_user_cap_amount}
              onChange={(e) => setForm({ ...form,
                minimum_user_cap_amount: e.target.value })} />
          </div>
          <div>
            <label className={label} htmlFor="cap-max">Maximum user cap (USD)</label>
            <input id="cap-max" className={input} type="text" inputMode="decimal"
              placeholder="1000"
              value={form.maximum_user_cap_amount}
              onChange={(e) => setForm({ ...form,
                maximum_user_cap_amount: e.target.value })} />
          </div>
        </div>
        <button onClick={saveConfig} disabled={busy === 'config'}
          className="px-4 py-2 text-sm font-medium text-white bg-brand-purple rounded-lg disabled:opacity-50">
          {busy === 'config' ? 'Saving...' : 'Save Policy'}
        </button>
      </div>

      {/* Step 2: per-credit price --------------------------------------- */}
      <div className={card}>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">
          Step 2: Per-credit price
        </h3>
        {!policyReady && (
          <p className="text-sm text-yellow-700 dark:text-yellow-400">
            Save the policy above first. Setting a price before the threshold and
            cap policy is saved returns &quot;On-Demand Usage is not available yet.&quot;
          </p>
        )}
        {policyReady && config?.local_dev_metered && !config?.metronome_configured && (
          <p className="text-sm text-yellow-700 dark:text-yellow-400">
            Metronome is not configured. In local development the price is saved
            here only (no external billing sync). For production, set
            METRONOME_BEARER_TOKEN and METRONOME_PRODUCT_ID in backend/.env.
          </p>
        )}
        <div className="text-sm text-gray-700 dark:text-gray-300">
          Current: <strong>{config?.unit_price ?? 'not set'}</strong>{' '}
          {config?.currency?.toUpperCase()} / credit
          {config?.rate_effective_at && (
            <span className="text-gray-500 dark:text-gray-400">
              {' '}(effective {new Date(config.rate_effective_at).toLocaleString()})
            </span>
          )}
        </div>

        <div className="flex items-end gap-2">
          <div className="flex-1 max-w-xs">
            <label className={label} htmlFor="metered-new-price">New price</label>
            <input id="metered-new-price" className={input} type="text"
              inputMode="decimal" placeholder="0.10"
              disabled={!policyReady}
              value={newPrice}
              onChange={(e) => { setNewPrice(e.target.value); setConfirmPrice(false); }} />
          </div>
          {!confirmPrice ? (
            <button onClick={() => setConfirmPrice(true)}
              disabled={!policyReady || !String(newPrice).trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-brand-purple rounded-lg disabled:opacity-50">
              Review Change
            </button>
          ) : null}
        </div>

        {confirmPrice && (
          <div className="p-3 rounded-lg border border-yellow-500/40 bg-yellow-500/10 space-y-2">
            <p className="text-sm text-gray-900 dark:text-white">
              Change price from <strong>{config?.unit_price ?? 'not set'}</strong>
              {' '}to <strong>{newPrice}</strong> per credit?
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Only future metered reservations will use the new price. Existing
              reservations keep the rate they were authorized at, and historical
              usage will not be repriced.
            </p>
            <div className="flex gap-2">
              <button onClick={changePrice} disabled={busy === 'price'}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-red rounded-lg disabled:opacity-50">
                {busy === 'price' ? 'Applying...' : 'Confirm'}
              </button>
              <button onClick={() => setConfirmPrice(false)}
                className="px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Health -------------------------------------------------------- */}
      {health && (
        <div className={card}>
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">
            Billing health
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            {[
              ['Active accounts', health.metered_active_accounts],
              ['Payment required', health.payment_required_accounts],
              ['Usage pending', health.pending],
              ['Retrying', health.retrying],
              ['Dead letter', health.dead_letter],
              ['Provisioning errors', health.provisioning_error_accounts],
              ['Threshold syncs pending', health.threshold_syncs_outstanding],
              ['Stale reservations', health.stale_metered_reservations],
            ].map(([name, value]) => (
              <div key={name}>
                <div className="text-xs text-gray-500 dark:text-gray-400">{name}</div>
                <div className={`text-xl font-bold ${
                  (name === 'Dead letter' || name === 'Provisioning errors')
                  && Number(value) > 0
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-gray-900 dark:text-white'}`}>
                  {value ?? 0}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Rate history -------------------------------------------------- */}
      <div className={card}>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">
          Rate history
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 dark:text-gray-400">
                <th className="py-1 pr-4">Price</th>
                <th className="py-1 pr-4">Currency</th>
                <th className="py-1 pr-4">Status</th>
                <th className="py-1 pr-4">Effective</th>
                <th className="py-1">Changed by</th>
              </tr>
            </thead>
            <tbody>
              {rates.length === 0 && (
                <tr><td colSpan="5" className="py-2 text-gray-500 dark:text-gray-400">
                  No rate has been configured yet.
                </td></tr>
              )}
              {rates.map((rate) => (
                <tr key={rate.id} className="border-t border-gray-200 dark:border-gray-700">
                  <td className="py-1 pr-4 text-gray-900 dark:text-white">{rate.unit_price}</td>
                  <td className="py-1 pr-4">{(rate.currency || '').toUpperCase()}</td>
                  <td className="py-1 pr-4">
                    <span className={rate.status === 'ACTIVE'
                      ? 'text-green-600 dark:text-green-400'
                      : rate.status === 'FAILED'
                        ? 'text-red-600 dark:text-red-400'
                        : 'text-gray-500 dark:text-gray-400'}>
                      {rate.status}
                    </span>
                  </td>
                  <td className="py-1 pr-4">
                    {rate.effective_at
                      ? new Date(rate.effective_at).toLocaleString() : '--'}
                  </td>
                  <td className="py-1">{rate.created_by_admin}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Accounts ------------------------------------------------------ */}
      <div className={card}>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">
          Metered accounts
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 dark:text-gray-400">
                <th className="py-1 pr-4">Account</th>
                <th className="py-1 pr-4">Status</th>
                <th className="py-1 pr-4">Hard cap</th>
                <th className="py-1 pr-4">Payment</th>
                <th className="py-1">Updated</th>
              </tr>
            </thead>
            <tbody>
              {accounts.length === 0 && (
                <tr><td colSpan="5" className="py-2 text-gray-500 dark:text-gray-400">
                  No account has enabled metered usage.
                </td></tr>
              )}
              {accounts.map((account) => (
                <tr key={account.user_id}
                  className="border-t border-gray-200 dark:border-gray-700">
                  <td className="py-1 pr-4 font-mono text-xs text-gray-900 dark:text-white">
                    {account.user_id}
                  </td>
                  <td className="py-1 pr-4">{account.metered_status}</td>
                  <td className="py-1 pr-4">{account.user_monthly_cap_amount ?? '--'}</td>
                  <td className="py-1 pr-4">
                    {/* Brand and last4 only: never a full card or a Stripe id. */}
                    {account.payment_method_last4
                      ? `${(account.payment_method_brand || 'card').toUpperCase()} ****${account.payment_method_last4}`
                      : 'none'}
                  </td>
                  <td className="py-1">
                    {account.updated_at
                      ? new Date(account.updated_at).toLocaleString() : '--'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MeteredBillingAdmin;
